import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Lottie from 'lottie-react'
import formSubmission2 from '../../../assets/lottie/formSubmission2.json'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import API from '../../../services/API'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../App'
import { SPEAK_UP_CATEGORIES } from './speakUpConstants'

// Staff-facing Freedom to Speak Up form. Anonymous by default: the server
// never stores who submitted an anonymous concern, so the reassurance below
// is true rather than aspirational.
const SpeakUp = () => {
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig ?? {}

  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const [anonymous, setAnonymous] = useState(true)
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [whenHappened, setWhenHappened] = useState('')
  const [whereHappened, setWhereHappened] = useState('')
  const [contactPreference, setContactPreference] = useState('')

  useEffect(() => {
    API.get('/speak-up/settings')
      .then((res) => {
        if (res.status === 200) setSettings(res.data)
        else setSettings({ enabled: false })
      })
      .finally(() => setLoading(false))
  }, [])

  const enabled =
    settings?.enabled ?? orgSettings?.speakUpSettings?.enabled !== false

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!description.trim()) {
      setToast({ message: 'Please describe your concern', severity: 'error' })
      return
    }
    setSubmitting(true)
    API.post('/speak-up', {
      anonymous,
      category: category || 'Other',
      description: description.trim(),
      whenHappened: whenHappened.trim(),
      whereHappened: whereHappened.trim(),
      contactPreference: anonymous ? '' : contactPreference.trim(),
      submittedVia: 'web',
    })
      .then((res) => {
        if (res.status === 200) {
          setResult(res.data)
        } else {
          setToast({
            message:
              res.data?.error ||
              'Unable to submit your concern. Please try again.',
            severity: 'error',
          })
        }
      })
      .finally(() => setSubmitting(false))
  }

  if (loading) return <LinearProgress />

  if (!enabled) {
    return (
      <Alert severity="info">
        <AlertTitle>Freedom to Speak Up is not enabled</AlertTitle>
        Your organisation has not switched on anonymous concern reporting in
        AmbuNet. If you have a concern, please speak to your line manager or use
        your organisation's own Freedom to Speak Up route.
      </Alert>
    )
  }

  if (result) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
          textAlign="center"
        >
          <Box maxWidth={260}>
            <Lottie animationData={formSubmission2} loop />
          </Box>
          <Typography variant="title3">Thank you for speaking up.</Typography>
          <Typography variant="body2" color="text.secondary">
            Your concern has been passed to the governance team.
          </Typography>
          <Chip
            label={`Reference ${result.referenceCode}`}
            color="primary"
            sx={{ fontWeight: 700, fontSize: '1rem', px: 1 }}
          />
          <Typography variant="caption" color="text.secondary" maxWidth={480}>
            {result.anonymous
              ? 'You raised this anonymously, so there is no way for us to follow up with you directly. Make a note of the reference if you would like to refer to it later.'
              : 'You chose to share your name, so the governance team may contact you to discuss it further.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{ mt: 1 }}
          >
            Return to Dashboard
          </Button>
        </Box>
      </Paper>
    )
  }

  const categories = settings?.categories ?? SPEAK_UP_CATEGORIES

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <RecordVoiceOverIcon color="primary" />
          <Typography variant="title2">Freedom to Speak Up</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Raise a concern about anything that worries you at work — patient
          safety, how people are treated, or wrongdoing. You can do this
          anonymously.
        </Typography>
      </Box>

      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}
      >
        <Stack gap={2.5}>
          {settings?.intro?.trim() ? (
            <Alert severity="info" icon={false} sx={{ whiteSpace: 'pre-wrap' }}>
              {settings.intro.trim()}
            </Alert>
          ) : (
            <Alert severity="info" icon={false}>
              This is not the incident reporting form. Use it when something
              does not feel right and you want it looked at — you do not need
              proof, and you will not be treated unfairly for raising it in good
              faith. If a patient is at immediate risk, tell someone on duty now
              as well.
            </Alert>
          )}

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: anonymous ? 'action.hover' : 'background.paper',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              gap={1}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <LockIcon
                  fontSize="small"
                  color={anonymous ? 'success' : 'disabled'}
                />
                <Box>
                  <Typography fontWeight={700}>
                    {anonymous
                      ? 'Raising this anonymously'
                      : 'Raising this in your name'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {anonymous
                      ? 'Your name and account are not recorded with this concern. Nobody, including site administrators, can see who raised it.'
                      : `This concern will be recorded as raised by ${user?.firstName} ${user?.lastName}, and the governance team may contact you.`}
                  </Typography>
                </Box>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={!anonymous}
                    onChange={(e) => setAnonymous(!e.target.checked)}
                  />
                }
                label="Share my name"
                sx={{ whiteSpace: 'nowrap', m: 0 }}
              />
            </Stack>
          </Paper>

          <FormControl fullWidth>
            <InputLabel>What is the concern about?</InputLabel>
            <Select
              value={category}
              label="What is the concern about?"
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Pick the closest fit — it only helps route the concern.
            </FormHelperText>
          </FormControl>

          <TextField
            label="Describe your concern"
            multiline
            minRows={6}
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText={
              anonymous
                ? 'Stick to what happened. Avoid details that would only be known to you if you want to stay anonymous.'
                : 'Stick to what happened, when, and who was affected.'
            }
          />

          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <TextField
              label="When did it happen? (optional)"
              placeholder="e.g. last Tuesday, or over the past few months"
              fullWidth
              value={whenHappened}
              onChange={(e) => setWhenHappened(e.target.value)}
            />
            <TextField
              label="Where? (optional)"
              placeholder="e.g. Kings Halton base, or a specific event"
              fullWidth
              value={whereHappened}
              onChange={(e) => setWhereHappened(e.target.value)}
            />
          </Stack>

          {!anonymous && (
            <TextField
              label="How would you like to be contacted? (optional)"
              placeholder="e.g. by phone in the evenings, or by email only"
              value={contactPreference}
              onChange={(e) => setContactPreference(e.target.value)}
            />
          )}

          <Divider />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={1}
          >
            <Typography variant="caption" color="text.secondary">
              Concerns are reviewed by the governance team. Anonymous concerns
              stay anonymous.
            </Typography>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit concern'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  )
}

export default SpeakUp
